/***
 *
 *   PROJECTS LIST
 *   Phase 8 — ProjectsSection card grid chrome. Keep create/delete modals.
 *
 **********/

import { useContext } from 'react';
import {
    useProjectContext,
    ViewContext,
    Card,
    SettingsWrapper,
    Button,
    Animate,
    useNavigate,
    useLocation,
    Badge,
    EmptyState,
    Skeleton,
    Icon,
    logoUrl,
} from 'components/lib';
import { useQueryClient } from 'react-query';
import { useTranslate } from 'app/translations';
import Style from './projects.tailwind';

export function Projects(props) {
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const context = useContext(ViewContext);
    const { projects, updateAllProjects, loading, selectAllProjects, selectedProject } =
        useProjectContext();

    function createProject() {
        context.modal.show(
            {
                title: _t('project.create.label'),
                form: {
                    logo: {
                        label: 'Logo',
                        type: 'logo',
                        required: false,
                        placeholder: '',
                        // Same bounds the server now enforces on POST
                        // /api/project, so an oversized or wrong-format logo
                        // fails here with a readable message instead of coming
                        // back as a 413/415. 'jpeg', not 'jpg' — the input
                        // compares the mime subtype.
                        accept: ['png', 'jpeg', 'webp'],
                        maxFileSize: 2,
                        max: 1,
                    },
                    name: {
                        label: _t('project.name'),
                        type: 'text',
                        required: true,
                    },
                    privacy: {
                        label: _t('project.privacy.label'),
                        type: 'select',
                        required: true,
                        options: [
                            { value: 'private', label: _t('project.privacy.private') },
                            { value: 'public', label: _t('project.privacy.public') },
                        ],
                        default: 'private',
                    },
                },
                buttonText: _t('save'),
                url: `/api/project`,
                method: 'POST',
            },
            () => {
                updateAllProjects(false);
                queryClient.invalidateQueries('projects');
            },
        );
    }

    function deleteProject(data) {
        const isCurrentProject = data.id === selectedProject?.id;
        context.modal.show(
            {
                title: _t('project.delete.label'),
                form: {},
                buttonText: _t('project.delete.label'),
                text: _t('project.delete.confirm', { name: data.name }),
                url: `/api/project/${data.id}`,
                method: 'DELETE',
                destructive: true,
            },
            () => {
                context.notification.show(
                    data.name + ' ' + _t('message.wasDeleted'),
                    'success',
                    true,
                );
                queryClient.invalidateQueries('projects');
                if (isCurrentProject) selectAllProjects();
            },
        );
    }

    function openProject(project) {
        navigate(`${location.pathname}/${project.id}`, {
            state: { title: project.name },
        });
    }

    return (
        <SettingsWrapper>
            <Animate>
                <div className={Style.stack}>
                    <div className={Style.header}>
                        <div>
                            <h1 className={Style.title}>{_t('project.manage.label')}</h1>
                            <p className={Style.description}>{_t('project.manage.title')}</p>
                        </div>
                        <Button
                            variant="accent"
                            size="sm"
                            icon="plus"
                            text={_t('project.create.label')}
                            action={createProject}
                        />
                    </div>

                    {loading ? (
                        <div className={Style.grid}>
                            <Skeleton className="h-36 w-full" />
                            <Skeleton className="h-36 w-full" />
                        </div>
                    ) : !projects?.length ? (
                        <EmptyState
                            icon="folder"
                            title={_t('project.label.multiple')}
                            text={_t('project.create.new')}
                            buttonText={_t('project.create.label')}
                            action={createProject}
                        />
                    ) : (
                        <div className={Style.grid}>
                            {projects.map((project) => {
                                const isPrivate = project.privacy === 'private';
                                return (
                                    <Card
                                        key={project.id}
                                        variant="interactive"
                                        className={Style.card}>
                                        <button
                                            type="button"
                                            className={Style.cardButton}
                                            onClick={() => openProject(project)}>
                                            <div className={Style.cardTop}>
                                                {/* `file_path` comes off the API
                                                    schemeless (bare `s3.../x.png`),
                                                    so a raw <img src> 404s and the
                                                    logo never appeared here.
                                                    logoUrl() adds the scheme — same
                                                    helper the project switcher and
                                                    the public intake page use. */}
                                                <div className={Style.mark}>
                                                    {project.file_path ? (
                                                        <img
                                                            src={logoUrl(project.file_path)}
                                                            alt=""
                                                            className={Style.logo}
                                                        />
                                                    ) : (
                                                        <Icon
                                                            image="folder"
                                                            size={18}
                                                            color="currentColor"
                                                        />
                                                    )}
                                                </div>
                                                <Badge
                                                    variant="neutral"
                                                    text={_t(
                                                        `project.privacy.${project.privacy || 'public'}`,
                                                    )}
                                                    icon={isPrivate ? 'lock' : 'globe'}
                                                />
                                            </div>
                                            <h2 className={Style.cardTitle}>{project.name}</h2>
                                            <p className={Style.cardMeta}>
                                                {project.users_count ?? 0}{' '}
                                                {_t('user.label.multiple').toLowerCase()}
                                            </p>
                                        </button>
                                        <div className={Style.cardActions}>
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                icon="edit"
                                                text={_t('project.edit.label')}
                                                action={() => openProject(project)}
                                            />
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                icon="trash"
                                                ariaLabel={_t('project.delete.label')}
                                                action={() => deleteProject(project)}
                                            />
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Animate>
        </SettingsWrapper>
    );
}
